<script setup lang="ts">
  import { createElementId } from '@common/core-layer/utils'

  import { ChevronSelectorVerticalIcon } from '@gohighlevel/ghl-icons/24/outline'

  import CPAvatar from '@ui/components/CPAvatar.vue'

  import CPButton from '@ui/components/CPButton.vue'

  import CPPopover from '@ui/components/CPPopover.vue'

  import CPSkeleton from '@ui/components/CPSkeleton.vue'

  import CPText from '@ui/components/CPText.vue'

  import { computed, watch } from 'vue'

  import { useI18n } from 'vue-i18n'

  import {
    UserCircleIcon,
    UserSquareIcon,
    Star05Icon,
    Users01Icon,
    CreditCard01Icon,
    LogOut01Icon,
  } from '@gohighlevel/ghl-icons/24/outline'
  import { useAuth } from '../../auth-layer/composables/useAuth'

  import { useNotificationHelper } from '../../core-layer/utils/toast.utils'

  import { useCurrentUser } from '../../current-user-layer/composables/useCurrentUser'

  import { useCPNavigation } from '../../navigation-layer/composables/useNavigationContext'

  import { DEFAULT_IMAGES } from '../../core-layer/constants'

  import { PROFILE_MENU_ITEMS, PROFILE_MENU_OPTIONS, type ProfileMenuOption } from '../constants/profile-menu'

  import { executeProfileMenuNavigation } from '../utils/profile-menu-navigation'

  import ProfileCardMenuPanel from './ProfileCardMenuPanel.vue'

  const props = defineProps({
    profileCardIsOpen: {
      type: Boolean,
      default: false,
    },
    /** `sidebar`: portal sidebar row + menu opens to the side; `default`: TopBar avatar + menu. */
    layout: {
      type: String,
      default: 'default',
      validator: (v: string) => ['default', 'sidebar'].includes(v),
    },
    /** When `layout` is sidebar, collapsed rail clips label/chevron; same trigger, overflow hidden. */
    isCollapsed: {
      type: Boolean,
      default: false,
    },
  })

  const emit = defineEmits(['update:profileCardIsOpen', 'logout'])

  const { t } = useI18n()

  const notification = useNotificationHelper()

  const { CPNavigateTo } = useCPNavigation()

  // Get user data - same as TopBar.vue
  const { store: currentUserStore } = useCurrentUser()

  const isProfileLoading = computed(() => currentUserStore.isLoadingProfile)

  const userProfile = computed(() => currentUserStore.profile)

  const profileTitle = computed(() => userProfile.value?.fullName || t('profile.unnamed'))

  const menuHeaderVariant = computed(() => (props.layout === 'sidebar' ? 'sidebar-row' : 'topbar-centered'))

  const menuCardId = computed(() => createElementId('profile', 'menu', 'card'))

  const profilePopoverId = computed(() => createElementId('profile', 'menu', 'popover'))

  const PROFILE_MENU_TELEPORT_ROOT_ID = 'cp-root-container'

  /** HLPopover `to` — under Client Portal root when present, else body. */
  const profileMenuTeleportTo = computed(() => {
    if (typeof document === 'undefined') {
      return 'body'
    }
    return document.getElementById(PROFILE_MENU_TELEPORT_ROOT_ID) ? `#${PROFILE_MENU_TELEPORT_ROOT_ID}` : 'body'
  })

  const menuPanelWrapClass = 'min-w-64 max-w-[min(18rem,calc(100vw-1rem))]'

  const popoverPlacement = computed(() => (props.layout === 'sidebar' ? 'right-start' : 'bottom-end'))

  /** Center trigger in icon rail; popover placement is wrong if menu stays open across expand/collapse. */
  const profilePopoverWrapClass = computed(() => (props.layout === 'sidebar' && props.isCollapsed ? 'flex w-full justify-center' : ''))

  const onProfilePopoverShowUpdate = (show: boolean) => {
    emit('update:profileCardIsOpen', show)
  }

  const handleLogout = async () => {
    useAuth().logout()
    emit('logout')
    emit('update:profileCardIsOpen', false)
  }

  async function handleProfileMenuSelect(option: ProfileMenuOption) {
    try {
      if (option === PROFILE_MENU_OPTIONS.LOGOUT) {
        await handleLogout()
        return
      }
      const item = PROFILE_MENU_ITEMS.find(i => i.key === option)
      if (!item) {
        emit('update:profileCardIsOpen', false)
        return
      }
      const opened = await executeProfileMenuNavigation(item.navigation, CPNavigateTo)
      if (!opened) {
        notification.showError({
          title: t('profileMenu.popupBlocked'),
          duration: 5000,
        })
      }
    } catch (error) {
      console.error('Error handling profile menu selection:', error)
    } finally {
      emit('update:profileCardIsOpen', false)
    }
  }

  watch(
    () => props.isCollapsed,
    () => {
      if (props.profileCardIsOpen) {
        emit('update:profileCardIsOpen', false)
      }
    }
  )
</script>

<template>
  <div class="relative w-full min-w-0">
    <!-- Profile Loading State -->
    <template v-if="isProfileLoading">
      <div v-if="layout === 'sidebar'" class="flex w-full min-w-0 justify-center">
        <div
          class="ease-linear flex h-10 min-w-0 items-center gap-2 overflow-hidden rounded-md px-0.5 transition-[width] duration-200"
          :class="isCollapsed ? 'w-10 max-w-10 shrink-0' : 'w-full'"
        >
          <CPSkeleton type="avatar" size="sm" class="flex-shrink-0" />
          <div class="min-w-0 flex-1 space-y-1.5 py-0.5">
            <CPSkeleton height="12px" width="80%" :round="true" />
            <CPSkeleton height="10px" width="60%" :round="true" />
          </div>
        </div>
      </div>
      <div v-else class="flex h-10 w-10 items-center justify-center rounded-full">
        <CPSkeleton type="avatar" size="sm" />
      </div>
    </template>

    <!-- Profile Loaded State -->
    <template v-else>
      <div
        :class="[profilePopoverWrapClass, 'group/profile-popover-wrap']"
        :data-collapsed="isCollapsed"
        :data-popover-open="profileCardIsOpen"
      >
        <CPPopover
          :id="profilePopoverId"
          :show="profileCardIsOpen"
          trigger="click"
          :placement="popoverPlacement"
          :show-arrow="false"
          :z-index="60"
          :to="profileMenuTeleportTo"
          @update:show="onProfilePopoverShowUpdate"
        >
          <template #trigger>
            <!-- Client Portal sidebar: same row for expanded/collapsed; collapsed clips text + chevron via overflow -->
            <CPButton
              v-if="layout === 'sidebar'"
              :id="createElementId('profile-sidebar', 'trigger', 'button')"
              size="sm"
              type="text"
              class="ease-linear flex min-w-0 items-center gap-2 overflow-hidden rounded-md text-left no-underline outline-none transition-[width,padding] duration-200 hover:bg-sidebar-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary w-full p-2 group-data-[collapsed=true]/profile-popover-wrap:p-0 group-data-[popover-open=true]/profile-popover-wrap:bg-sidebar-hover"
              :title="t('profile.menu.title')"
              :aria-label="t('profile.menu.title')"
              :aria-expanded="profileCardIsOpen"
              :aria-controls="menuCardId"
              :aria-haspopup="true"
            >
              <template #iconLeft>
                <CPAvatar
                  :id="createElementId('profile-sidebar', 'avatar', 'image')"
                  :src="userProfile?.avatar"
                  :title="profileTitle"
                  :name="profileTitle"
                  :img-props="{ alt: profileTitle }"
                  shape="square"
                  size="sm"
                  class="flex-shrink-0"
                />
              </template>
              <div class="grid min-w-0 flex-1 text-left">
                <CPText size="lg" weight="medium" htmlTag="span" class="text-body truncate !leading-tight">
                  {{ profileTitle }}
                </CPText>
                <CPText size="sm" htmlTag="span" class="text-600 truncate !leading-tight">
                  {{ userProfile?.email || t('profile.emailPlaceholder') }}
                </CPText>
              </div>
              <template #iconRight>
                <ChevronSelectorVerticalIcon class="text-body" />
              </template>
            </CPButton>

            <!-- TopBar: circle avatar -->
            <CPButton
              v-else
              :id="createElementId('profile', 'avatar', 'button')"
              size="sm"
              type="text"
              class="flex h-10 w-10 items-center justify-center rounded-full p-0 outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary group-data-[popover-open=true]/profile-popover-wrap:bg-sidebar-hover"
              :title="t('profile.menu.title')"
              :aria-label="t('profile.menu.title')"
              :aria-expanded="profileCardIsOpen"
              :aria-controls="menuCardId"
              :aria-haspopup="true"
            >
              <CPAvatar
                :id="createElementId('profile', 'avatar', 'image')"
                :src="userProfile?.avatar"
                :title="profileTitle"
                :name="profileTitle"
                :img-props="{
                  alt: profileTitle,
                }"
                shape="circle"
                size="sm"
                class="flex-shrink-0"
              />
            </CPButton>
          </template>

          <div :class="menuPanelWrapClass">
            <ProfileCardMenuPanel
              :card-id="menuCardId"
              :header-variant="menuHeaderVariant"
              :user-profile="userProfile"
              :profile-title="profileTitle"
              @select="handleProfileMenuSelect"
              @close="emit('update:profileCardIsOpen', false)"
            >
              <template #extras-banner="{ close }">
                <slot name="profile-menu-extras-banner" :close="close" />
              </template>
              <template #extras-items="{ close }">
                <slot name="profile-menu-extras-items" :close="close" />
              </template>
            </ProfileCardMenuPanel>
          </div>
        </CPPopover>
      </div>
    </template>
  </div>
</template>

<style scoped>
  :deep(#profile-sidebar-trigger-button .hr-button__content .hr-icon-left),
  :deep(#profile-sidebar-trigger-button .hr-button__content .hr-icon-left svg) {
    height: 2rem;
    width: 2rem;
  }

  :deep(#profile-sidebar-trigger-button .hr-button__content) {
    width: 100% !important;
  }

  :deep(#profile-sidebar-trigger-button .hr-button__content .hr-icon-right) {
    margin-left: auto !important;
  }
</style>
